import React from 'react';
import { IconButton, TextField } from '@material-ui/core';
import { Close } from '@material-ui/icons';
import { Control, Controller, UseFormSetValue, useWatch } from 'react-hook-form';
import { EditableQuestion, StructuredInputType } from '../../../../../types';
import FieldLimits from './FieldLimits';
import { StyledFieldGrid } from './styles';

interface Props {
  control: Control<EditableQuestion>;
  setValue: UseFormSetValue<EditableQuestion>;
  toggleIndex: number;
  index: number;
  fieldId: string;
  onRemove: () => void;
}

const FieldRow = ({ control, setValue, toggleIndex, index, fieldId, onRemove }: Props) => {
  const path = `input.toggles.${toggleIndex}.fields.${index}` as const;

  const inputType: StructuredInputType | undefined = useWatch({
    control,
    name: `${path}.input_type` as 'input.toggles.0.fields.0.input_type',
  });

  // The api forbids decimals, min and max on a text field, so choosing a text keyboard
  // has to clear anything already typed into them rather than just hide the boxes.
  const clearNumericLimits = () => {
    (['decimals', 'min', 'max'] as const).forEach(limit => {
      setValue(`${path}.${limit}` as 'input.toggles.0.fields.0.min', undefined);
    });
  };

  const handleInputTypeChange = (onChange: (value: string) => void) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    onChange(event.target.value);

    if (event.target.value === 'text') {
      clearNumericLimits();
    }
  };

  return (
    <StyledFieldGrid>
      <Controller
        name={`${path}.id` as 'input.toggles.0.fields.0.id'}
        control={control}
        render={({ field: { onChange, ref, ...restField } }) => (
          <TextField
            inputRef={ref}
            {...restField}
            placeholder="Field id"
            onChange={event => onChange(event.target.value)}
          />
        )}
      />
      <Controller
        name={`${path}.input_type` as 'input.toggles.0.fields.0.input_type'}
        control={control}
        render={({ field: { onChange, ref, ...restField } }) => (
          <TextField
            inputRef={ref}
            {...restField}
            select
            SelectProps={{ native: true }}
            inputProps={{ 'aria-label': 'Keyboard' }}
            onChange={handleInputTypeChange(onChange)}
          >
            <option value="number">Number</option>
            <option value="text">Text</option>
          </TextField>
        )}
      />
      <Controller
        name={`${path}.suffix` as 'input.toggles.0.fields.0.suffix'}
        control={control}
        render={({ field: { onChange, ref, value, ...restField } }) => (
          <TextField
            inputRef={ref}
            {...restField}
            value={value ?? ''}
            placeholder="Suffix"
            onChange={event => onChange(event.target.value)}
          />
        )}
      />
      <FieldLimits control={control} toggleIndex={toggleIndex} index={index} inputType={inputType} />
      <IconButton aria-label={`Remove field ${fieldId || index + 1}`} onClick={onRemove}>
        <Close />
      </IconButton>
    </StyledFieldGrid>
  );
};

export default FieldRow;
